import { h, Component } from 'preact';
import { Localizer, Text, withText } from 'preact-i18n';
import { Icon, InputText, Button } from '@cube/blocks';
import { trackingProvider, trackEvent, INPUT_KEYS, doNotTrack, DIMENSION, EVENT } from '@cube/track';
import cx from 'classnames';
import style from './style.less';

const LOGO_POSITION_INSIDE_RIGHT = 'provider_inside_right';
const LOGO_POSITION_INSIDE_LEFT = 'provider_inside_left';
const LOGO_POSITION_LEFT = 'provider_left';

@trackingProvider({
	[DIMENSION.componentpath]: 'SearchInput'
})
export default class SearchInput extends Component {

	state = {
		queryValue: ''
	};

	trackFromSuggestion = false;

	handleInput = queryValue => {
		this.setState({ queryValue });
		this.props.onInput && this.props.onInput(queryValue);
	};

	/**
	 * Submit the form if a searchURL is provided
	 */
	handleSubmit = e => {
		const { onSubmit, preventEmptySubmit, searchURL } = this.props;
		const { queryValue } = this.state;

		//prevent empty submissions if that flag is set on the props or
		if (preventEmptySubmit && !queryValue.trim()) return e && e.preventDefault();

		//fire the onSubmit handler if provided
		if (onSubmit) onSubmit({ form: this.base, data: new FormData(this.base) });

		//if a searchURL is provided and this is a "synthetic" submit handler, fire a real form submit
		if (searchURL && !(e && e.type === 'submit') ) {
			this.base.submit();
		}
		else if (!searchURL && e && e.type === 'submit') {
			//prevent an actual form submission if no searchURL is provided
			e.preventDefault();
		}

		if (e.type === 'submit') {
			trackEvent(this.context, {
				[INPUT_KEYS.action]: EVENT.search,
				[DIMENSION.searchterm]: queryValue
			});
		}
	};

	/**
	 * keep ref of parent form to check if click
	 */
	handleSearchInputRef = ref => this.searchInputRef=ref;

	/**
	 * callback to user finalizing search query with suggested search from dropdown.
	 * @param {String} queryValue		suggested search query to submit
	 */
	handleSuggestionClick = queryValue => {
		this.trackFromSuggestion = true;
		this.setState({ queryValue }, this.handleSubmit);
	};

	/**
	 * looks in the URL for a seach query and sets it to state if present.
	 */
	setUrlQuery = () => {
		let urlParams = window.location.search.substring(1)
			.split('&')
			.reduce((allParams, currParam) => {
				currParam = currParam.split('=');
				allParams[currParam[0]] = currParam[1];
				return allParams;
			}, {});
		if (!urlParams || !urlParams.q) return;
		let queryValue = decodeURIComponent(urlParams.q)
			.replace(/\+/g, ' ');

		this.setState({ queryValue });
	};

	static defaultProps = {
		searchURL: '',
		logoImage: false,
		providerInputSuffix: '',
		queryParameter: 'q',
		providerLogoPosition: 'none',
		enablePlaceholderText: false,
		placeholderText: '',
		autoUpdateQuery: true,
		forceFocus: false,
		preventEmptySubmit: true,
		buttonStyleRandomization: true,
		fullFormFocus: true,
		searchButtonGlyphIcon: false,
		showMagnifyGlyphAndHideInput: false,
		useSASS: false,
		openSearchInNewWindow: false,
		showOverlayOnFocus: true,
		openInNewWindowSASSKey: 'search.features.input.open_in_new_tab', // TODO: Use this to get value from SASS
		clearInputOnNewWindowSearch: true,
		hiddenParameters: [],
		searchSuggestions: []
	};

	componentWillMount() {
		if (window.location.search.length > 1) {
			this.setUrlQuery();
		}
	}

	render({ class: classProp, compress, icon, inputPeers, inputTextClass, inputWrapperClass, searchURL, style: styleProp, openSearchInNewWindow, providerLogoPosition, providerInputSuffix, logoImage, placeHolderText,
		context, queryParameter, hiddenParameters, searchSuggestions, showOverlay, suggestionsFooter, suggestionsWidthTarget, buttonClass, forceFocus }, { queryValue }) {
		//compress implies icon
		if (compress) icon=true;

		let extraFormProps = {};
		classProp && (extraFormProps.class = classProp);
		styleProp && (extraFormProps.style = styleProp);

		return (
			<form
				action={searchURL}
				role="search"
				target={openSearchInNewWindow ? '_blank' : '_self'}
				onSubmit={this.handleSubmit}
				ref={this.handleSearchInputRef}
				{...extraFormProps}
			>
				<div role="group" class={cx(style.wrapper)}>
					{providerLogoPosition === LOGO_POSITION_LEFT &&
						<ProviderLogo compress={compress} providerInputSuffix={providerInputSuffix} logoImage={logoImage} providerLogoPosition={providerLogoPosition} />
					}
					<div class={cx(style.inputWrapper, inputWrapperClass)}>
						<Localizer>
							<InputText
								type="search"
								class={cx(style.inputText, inputTextClass, compress && style.compress, style[providerLogoPosition])}
								inputClass={compress && style.compress}
								listClass={cx(showOverlay && style.mobileSuggestion)}
								name={queryParameter}
								value={queryValue}
								placeholder={placeHolderText ? <Text id={placeHolderText}>{placeHolderText}</Text> : ''}
								onInput={this.handleInput}
								onChange={this.handleSuggestionClick}
								autocomplete="off"
								showOverlay={showOverlay}
								suggestionsFooter={suggestionsFooter}
								suggestionsWidthTarget={suggestionsWidthTarget}
								showClear
								clearTextClass={style.clearMe}
								forceFocus={forceFocus}
								{...doNotTrack()} //click events are redundant: we're tracking events manually instead.
							>
								{searchSuggestions}
							</InputText>
						</Localizer>
						{(providerLogoPosition === LOGO_POSITION_INSIDE_LEFT) &&
							<ProviderLogo compress={compress} providerInputSuffix={providerInputSuffix} logoImage={logoImage} providerLogoPosition={providerLogoPosition} />
						}
						{providerLogoPosition === LOGO_POSITION_INSIDE_RIGHT &&
							<ProviderLogo providerInputSuffix={providerInputSuffix} logoImage={logoImage} providerLogoPosition={providerLogoPosition} />
						}
						{ inputPeers }
					</div>
					<SearchButton compress={compress} icon={icon} class={buttonClass} onClick={this.handleSubmit} />
					<input type="hidden" name="context" value={context} />
					{hiddenParameters.map(({ name, value }) => <input type="hidden" name={name} value={value} />) }
				</div>
			</form>
		);
	}
}

function SearchButton({ compress, icon, class: cls, ...restProps }) {
	return (
		<Button class={cx(style.search, compress && style.compress, icon && style.icon, cls)} {...restProps} {...doNotTrack()} >
			{ icon ? <Icon name="search-cube" class={style.icon} /> : <Text id="search" /> }
		</Button>
	);
}

@withText({
	providerLogoAltText: 'providerLogo'
})
class ProviderLogo extends Component {
	render({ compress, providerLogoPosition, providerInputSuffix, logoImage, providerLogoAltText }) {
		return (
			<span class={cx(style.provider, compress && style.compress, style[providerLogoPosition], style[providerInputSuffix])}>
				<span class={style.verticalCenter} />
				<img src={logoImage} alt={providerLogoAltText} />
			</span>
		);
	}
}
